/**
 * Fuzzy matching for the quick switcher (SPEC-2026-07-internal-chat, T-22). The highlight and
 * excerpt helpers moved to lib-trend-core (`text-highlight.util`) so the inbox message search
 * shares them; they are re-exported here for the existing chat call sites.
 *
 * Scores only RANK candidates the caller already holds; nothing here widens
 * what the user may see.
 */
import { normalizeSearchText } from 'lib-trend-core';

export { buildExcerpt, highlightSegments, normalizeSearchText, normalizeText } from 'lib-trend-core';
export type { HighlightSegment as ChatHighlightSegment } from 'lib-trend-core';

/**
 * Match score of `text` against `term`, or `-1` when it does not match at all.
 * Higher is better: exact prefix > word start > substring > subsequence.
 * An empty term matches everything with score `0` (keeps the caller's order).
 */
export function fuzzyScore(text: string, term: string): number {
  const haystack = normalizeSearchText(text);
  const needle = normalizeSearchText(term);

  if (!needle) return 0;
  if (!haystack) return -1;

  const index = haystack.indexOf(needle);
  if (index === 0) return 1000 - haystack.length;
  if (index > 0) {
    const isWordStart = haystack.charAt(index - 1) === ' ';
    return (isWordStart ? 800 : 600) - index;
  }

  // Subsequence: every character of the term appears in order (typo-tolerant
  // enough for "chnl" → "channel" without a scoring library).
  let cursor = 0;
  let gaps = 0;
  for (const char of needle) {
    const found = haystack.indexOf(char, cursor);
    if (found < 0) return -1;
    gaps += found - cursor;
    cursor = found + 1;
  }

  return Math.max(1, 400 - gaps);
}
